// src/components/CombinedGastosView.jsx
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import moment from "moment";
moment.locale("es");

// — Función para leer el balance guardado
async function fetchSavedDash() {
  const ref = doc(db, "resumenBalances", "latest");
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

export default function CombinedGastosView({ orderIds }) {
  const { formatCurrency } = useFunctionsPrevia();

  // Estado para la tabla
  const [ordersData, setOrdersData] = useState([]);

  // Estado para el dashboard
  const [dash, setDash] = useState({
    ingresos: 0,
    gastos:   0,
    balance:  0,
    totalCarpetas: 0,
  });

  const [isLoading, setIsLoading] = useState(false);

  // 1) Cargo la tabla (metadatos de cada orden)
  useEffect(() => {
    if (!orderIds || orderIds.length === 0) {
      setOrdersData([]);
      return;
    }
    (async () => {
      const metas = await Promise.all(
        orderIds.map(async (oid) => {
          const snap = await getDoc(doc(db, "pedidos", oid));
          if (!snap.exists()) return null;
          const d = snap.data();
          return {
            id:      oid,
            numero:  d.numeroOrdenDeCompra,
            fecha:   d.fechaOrden,
            cliente: d.nombreCliente,
            total:   Number(d.total || 0),
            divisa:  d.divisa,
          };
        })
      );
      setOrdersData(metas.filter(Boolean));
    })();
  }, [orderIds]);

  // 2) Al montar, cargo el último dash guardado
  useEffect(() => {
    (async () => {
      const saved = await fetchSavedDash();
      if (saved) {
        setDash({
          ingresos:      saved.ingresos,
          gastos:        saved.gastos,
          balance:       saved.balance,
          totalCarpetas: saved.totalCarpetas,
        });
      }
    })();
  }, []);

  // 3) Cuando ordersData cambia, recalculo + persisto
  useEffect(() => {
    if (!ordersData.length) {
      setDash({ ingresos: 0, gastos: 0, balance: 0, totalCarpetas: 0 });
      return;
    }
 
    setIsLoading(true);
 
    (async () => {
      // 1) ingresos
      const ingresos = ordersData.reduce((sum, o) => sum + o.total, 0);
 
      // 2) gastos en paralelo
      const gastosPorOrden = await Promise.all(
        ordersData.map(async ({ id: oid }) => {
          const capsSnap = await getDocs(collection(db, `pedidos/${oid}/listasGastos`));
          const gastosEnEstaOrden = await Promise.all(
            capsSnap.docs.map(async (cap) => {
              const prodSnap = await getDocs(
                collection(db, `pedidos/${oid}/listasGastos/${cap.id}/productosGastos`)
              );
              return prodSnap.docs.reduce((s, d) => {
                const { cantidad = 0, precioUnitario = 0 } = d.data();
                return s + cantidad * precioUnitario;
              }, 0);
            })
          );
          return gastosEnEstaOrden.reduce((a, b) => a + b, 0);
        })
      );
      const gastos = gastosPorOrden.reduce((a, b) => a + b, 0);
 
      // 3) nuevo dash
      const newDash = {
        ingresos,
        gastos,
        balance: ingresos - gastos,
        totalCarpetas: ordersData.length,
      };
 
      setDash(newDash);
      setIsLoading(false);
 
      // 4) persisto
      try {
        await setDoc(
          doc(db, "resumenBalances", "latest"),
          { ...newDash, orderIds, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch (err) {
        console.error("Error guardando balance:", err);
      }
    })();
  }, [ordersData, orderIds]);

  // Datos para la gráfica
  const barData = [
    { name: "Ingresos", value: dash.ingresos },
    { name: "Gastos",   value: dash.gastos   },
  ];

  const fmtDate = (d) =>
    moment(d).isValid() ? moment(d).format("DD MMM YY") : "-";

  return (
    <div className="combined-gastos-view">
      {/* ─── RESUMEN ─── */}
      <div className="summary-grid">
        {isLoading ? (
          // tu spinner / skeleton aquí
          <div className="loading-dash">Calculando balance…</div>
        ) : (
          [
            { t: "Balance",  v: dash.balance,   up: dash.balance >= 0 },
            { t: "Ingresos", v: dash.ingresos,  up: true },
            { t: "Gastos",   v: dash.gastos,    up: false },
          ].map(({ t, v, up }) => (
            <div key={t} className="summary-card">
              <div className="card-title">{t}</div>
              <div className="card-value">{formatCurrency(v)}</div>
              <div className={`card-change ${up ? "up" : "down"}`}>
                {up ? "▲" : "▼"} 12%
              </div>
            </div>
          ))
        )}
      </div>

      {/* ─── GRÁFICA + TABLA ─── */}
      <div className="chart-grid gastos-dashboard">
        {/* Gráfica */}
        <div className="panel chart-panel">
          <h4 style={{ margin: "0 0 26px" }}>Ingresos vs Gastos</h4>
          {dash.ingresos === 0 && dash.gastos === 0 ? (
            <div style={{ padding: 50, textAlign: "center", opacity: 0.5 }}>
              Sin datos en la gráfica
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={390}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis
                  width={120}
                  tickMargin={6}
                  domain={[0, "dataMax"]}
                  tickFormatter={(v) =>
                    new Intl.NumberFormat("es-MX", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(v)
                  }
                />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="value">
                  {barData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#4caf50" : "#f44336"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Tabla de Órdenes */}
        <div className="panel trans-panel">
          <h4>Órdenes seleccionadas</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "center", padding: 8 }}>No. Orden</th>
                <th style={{ textAlign: "center", padding: 8 }}>Fecha</th>
                <th style={{ textAlign: "center", padding: 8 }}>Cliente</th>
                <th style={{ textAlign: "right", padding: 8 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {ordersData.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    style={{ textAlign: "center", padding: 16, opacity: 0.6 }}
                  >
                    No hay órdenes seleccionadas
                  </td>
                </tr>
              ) : (
                ordersData.map((o) => (
                  <tr key={o.id}>
                    <td style={{ textAlign: "center", padding: 8 }}>
                      {o.numero?.toString().padStart(4, "0")}
                    </td>
                    <td style={{ textAlign: "center", padding: 8 }}>
                      {fmtDate(o.fecha)}
                    </td>
                    <td style={{ textAlign: "center", padding: 8 }}>
                      {o.cliente}
                    </td>
                    <td style={{ textAlign: "right", padding: 8 }}>
                      {formatCurrency(o.total, o.divisa)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {ordersData.length > 0 && (
              <tfoot>
                <tr>
                  <td
                    colSpan={3}
                    style={{ textAlign: "right", padding: 8, fontWeight: 600 }}
                  >
                    Total:
                  </td>
                  <td
                    style={{ textAlign: "right", padding: 8, fontWeight: 600 }}
                  >
                    {formatCurrency(
                      ordersData.reduce((sum, o) => sum + o.total, 0)
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}